import React from 'react';
import { useTheme, Box, Typography, CircularProgress } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { VehicleAutoSearch, AutoSearchState } from '../../models/vehicleAutoSearch';
import { useTranslation } from 'react-i18next';
import { getAutoSearchStatusColor, getAutoSearchStatusLabel } from '../../services/autoSessionService';
import BaseBadge from './BaseBadge';

interface AutoSearchBadgeProps {
  session: VehicleAutoSearch | null;
  loading?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium';
  showLabel?: boolean;
}

const AutoSearchBadge: React.FC<AutoSearchBadgeProps> = ({ 
  session, 
  loading, 
  onClick, 
  size = 'small',
  showLabel = false 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isSearchOn = session?.autoSearchState === AutoSearchState.ON;
  const statusColor = getAutoSearchStatusColor(theme, session);
  const statusLabel = getAutoSearchStatusLabel(t, session, loading);

  if (showLabel) {
    const getStatusText = () => {
      if (loading) return t('Loading...');
      if (!session) return t('OFF');
      switch (session.autoSearchState) {
        case AutoSearchState.ON:
          return t('Active');
        case AutoSearchState.ERRORED:
          return t('Error');
        case AutoSearchState.OFF:
        default:
          return t('Inactive');
      }
    };

    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${isSearchOn ? theme.palette.border.green : theme.palette.border.medium}`,
          backgroundColor: isSearchOn 
            ? 'rgba(34, 197, 94, 0.05)' 
            : theme.palette.mode === 'dark' 
              ? 'transparent' 
              : 'background.white',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': onClick ? {
            borderColor: isSearchOn ? theme.palette.border.green : theme.palette.border.medium,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          } : {},
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: statusColor,
            transition: 'color 0.2s ease',
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : (
            <SearchIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.dark',
              fontSize: '0.8125rem',
              mb: 0.25,
            }}
          >
            {t('Auto Search')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: statusColor,
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {getStatusText()}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <BaseBadge
      icon={<SearchIcon />}
      loading={loading}
      onClick={onClick}
      size={size}
      tooltipTitle={statusLabel}
      statusColor={statusColor}
      isActive={isSearchOn}
      activeBackgroundColor="action.hover"
      activeHoverColor="border.green"
    />
  );
};

export default AutoSearchBadge;
